import React from "react";
// Rate Management dashboard

// Default
import { useNavigate } from "react-router-dom";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import CustomChip from "../chip/Chip";
import CustomSearch from "../search/Search";
import CustomDataTable from "../table/CustomTable";
import DashboardCard from "../card/DashboardCard";
import RateManagementBarChart from "../charts/RateManagementBarChart";

// Mui
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";

// React icons
import { FaRegClock } from "react-icons/fa";
import { CiCircleList } from "react-icons/ci";
import { FaRegCalendarAlt } from "react-icons/fa";
import { IoMdSettings } from "react-icons/io";
import { FiFileText } from "react-icons/fi";
import { MdEdit } from "react-icons/md";
import { MdDelete } from "react-icons/md";
import { IconType } from "react-icons";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

// Types
interface StatsData {
  title: string;
  amount: string;
  subTitle: string;
  icon: IconType;
}

interface RateSchedule {
  id: string;
  name: string;
  category: string;
  status: string;
  customers: number;
  lastUpdated: string;
  effectiveFrom: string;
  hasSeasonalRates: boolean;
  hasTOU: boolean;
}

interface SelectOption {
  value: string;
  label: string;
}

const COLUMNS = [
  "Rate ID",
  "Name",
  "Category",
  "Status",
  "Customers",
  "Last Updated",
  "Type",
  "Actions",
];

const statsData: StatsData[] = [
  {
    title: "Active Rate Schedules",
    amount: "8",
    subTitle: "2 pending approval",
    icon: CiCircleList,
  },
  {
    title: "TOU Rate Schedules",
    amount: "3",
    subTitle: "1401 customers",
    icon: FaRegClock,
  },
  {
    title: "Seasonal Rates",
    amount: "4",
    subTitle: "9787 customers",
    icon: FaRegCalendarAlt,
  },
  {
    title: "Pending Rate Changes",
    amount: "2",
    subTitle: "Effective next month",
    icon: IoMdSettings,
  },
];

const rateSchedules: RateSchedule[] = [
  {
    id: "RS001",
    name: "Residential Standard",
    category: "Residential",
    status: "Active",
    customers: 8542,
    lastUpdated: "2024-01-15",
    effectiveFrom: "2024-01-01",
    hasSeasonalRates: true,
    hasTOU: false,
  },
  {
    id: "RS002",
    name: "Commercial TOU",
    category: "Commercial",
    status: "Active",
    customers: 1245,
    lastUpdated: "2024-01-10",
    effectiveFrom: "2024-01-01",
    hasSeasonalRates: true,
    hasTOU: true,
  },
  {
    id: "RS003",
    name: "Industrial Demand",
    category: "Industrial",
    status: "Active",
    customers: 156,
    lastUpdated: "2024-01-05",
    effectiveFrom: "2024-01-01",
    hasSeasonalRates: false,
    hasTOU: true,
  },
];

const categoryOptions: SelectOption[] = [
  { value: "all", label: "All Categories" },
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "industrial", label: "Industrial" },
];

const statusOptions: SelectOption[] = [
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
  { value: "inactive", label: "Inactive" },
];

const chipColor = (status: string): string => {
  if (status === "Active") return "#9CE976";
  if (status === "Processing") return "#edb64a";
  if (status === "Pending") return "#0396FF";
  return "#999B9D";
};

const RateManagementDashboard: React.FC = () => {
  const navigate = useNavigate();

  const rowsContent = rateSchedules?.map(
    (item: RateSchedule, index: number) => (
      <TableRow
        key={index}
        sx={{
          height: "50px",
          background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
        }}
        className="custom_table_tbody"
      >
        <TableCell sx={{ padding: 0 }}>
          <Typography className="custom_table_tbody_cell">{item.id}</Typography>
        </TableCell>
        <TableCell sx={{ padding: 0 }}>
          <Typography className="custom_table_tbody_cell">
            {item.name}
          </Typography>
        </TableCell>
        <TableCell sx={{ padding: 0 }}>
          <Typography className="custom_table_tbody_cell">
            {item.category}
          </Typography>
        </TableCell>
        <TableCell sx={{ padding: 0 }}>
          <Typography className="custom_table_tbody_cell">
            <CustomChip
              label={item.status}
              backgroundColor={chipColor(item.status)}
            />
          </Typography>
        </TableCell>
        <TableCell sx={{ padding: 0 }}>
          <Typography className="custom_table_tbody_cell">
            {item.customers.toLocaleString()}
          </Typography>
        </TableCell>
        <TableCell sx={{ padding: 0 }}>
          <Typography className="custom_table_tbody_cell">
            {item.lastUpdated}
          </Typography>
        </TableCell>
        <TableCell sx={{ padding: 0 }}>
          <Typography className="custom_table_tbody_cell">
            {item.hasSeasonalRates && "Seasonal"}
            {item.hasTOU && "TOU"}
          </Typography>
        </TableCell>
        <TableCell sx={{ padding: 0 }}>
          <Stack direction={"row"} alignContent={"center"} gap={"15px"}>
            <UnifiedIcon icon={MdEdit} color="#999B9D" fontSize="16px" />
            <UnifiedIcon icon={FiFileText} color="#999B9D" fontSize="16px" />
            <UnifiedIcon icon={MdDelete} color="#999B9D" fontSize="18px" />
          </Stack>
        </TableCell>
      </TableRow>
    )
  );

  const statsCard = ({
    title,
    amount,
    subTitle,
    icon,
  }: {
    title: string;
    amount: string;
    subTitle: string;
    icon: IconType;
  }) => {
    return (
      <Card>
        <CardContent>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Stack>
              <Typography variant="h5">{title}</Typography>
              <Typography variant="body1" mt={"10px"} fontWeight={700}>
                {amount}
              </Typography>
              <Typography variant="caption" mt={"10px"}>
                {subTitle}
              </Typography>
            </Stack>
            <UnifiedIcon icon={icon} color="#999B9D" fontSize="30px" />
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <Card>
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack>
            <Stack>
              <Typography variant="h5">Rate Management</Typography>
              <Typography variant="caption" color="#7D808F" mt={"2px"}>
                Manage rate schedules and configurations
              </Typography>
            </Stack>
          </Stack>
          <Stack direction={"row"} gap={"20px"} alignItems={"center"}>
            <UnifiedIcon icon={FaRegClock} color="#999B9D" fontSize="18px" />
            <ContainedButton
              label={"New Rate Schedule"}
              sx={{
                width: "fit-content",
                fontWeight: 600,
                color: "#ffffff",
              }}
              onClick={() => {
                navigate("/tariff-form");
              }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2.25} mt={"20px"}>
        {statsData.map((item: StatsData, index: number) => {
          return (
            <Grid key={index} size={{ xs: 12, md: 3, lg: 3 }}>
              {statsCard(item)}
            </Grid>
          );
        })}
      </Grid>

      <Card sx={{ mt: "20px", maxHeight: "650px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Rate Schedules</Typography>
              <Stack
                direction={"row"}
                width={"80%"}
                justifyContent={"flex-end"}
                gap={"10px"}
                alignItems={"center"}
              >
                <Box sx={{ width: "25%" }}>
                  <CustomSearch
                    placeholder={"Search rate schedules..."}
                    sx={{ width: "100%", height: "43px" }}
                  />
                </Box>
                <Box sx={{ width: "25%" }}>
                  <CustomOutlinedSelect
                    placeholder={"Category"}
                    options={categoryOptions}
                  />
                </Box>
                <Box sx={{ width: "25%" }}>
                  <CustomOutlinedSelect
                    placeholder={"Status"}
                    options={statusOptions}
                  />
                </Box>
              </Stack>
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <CustomDataTable
            sx={{ maxHeight: "600px" }}
            columns={COLUMNS}
            pagination={false}
            rowsContent={rowsContent}
          />
        </CardContent>
      </Card>

      <Box mt={"20px"}>
        <DashboardCard
          title={"Category Distribution"}
          showIcons={false}
          sx={{ padding: "12px 14px", height: "480px" }}
          body={<RateManagementBarChart />}
        />
      </Box>
    </Layout>
  );
};

export default RateManagementDashboard;
